import { StatusBar, Modal } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import styles from './ProductPublishModal.module.scss';
import { useNavigate } from 'react-router-dom';
import chefPlating from 'src/assets/images/chef-plating.png';
import { SelectPublishType } from './steps/SelectPublishType/SelectPublishType';
import { ExcludeProducts } from './steps/ExcludeProducts';
import { SelectPriceScheme } from './steps/SelectPriceScheme';
import { SummaryView } from './steps/SummaryView';
import { IPublishFormValues, PublishType, Steps } from './types';
import { useState } from 'react';
import { SelectVenues } from './steps/SelectVenues/SelectVenues';
import { usePublishProducts } from 'src/hooks/usePublishProducts/usePublishProducts';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { PublishProductsType } from 'src/api/publishProducts/types';
import { successNotification, errorNotification } from './Notifications';
import { ScheduleView } from '../ScheduleView/ScheduleView';

interface IProductPublishModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

const Content = ({ closeModal }: Omit<IProductPublishModalProps, 'isOpen'>) => {
  const [step, setStep] = useState<Steps>(Steps.type);
  const [publishRequest, setPublishRequest] = useState<IPublishFormValues>({
    type: PublishType.PRODUCT_AND_PRICING,
    priceSchemeId: null,
    excludedItems: [],
    venues: [],
    publicationDate: null,
  });
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { trackEvent, captureException } = useUserTracking();
  const navigate = useNavigate();
  const publishProducts = usePublishProducts();

  const saveData = (data: Partial<IPublishFormValues>, nextStep?: Steps) => {
    setPublishRequest((prev) => ({ ...prev, ...data }));
    if (nextStep === undefined) {
      setStep((prev) => prev + 1);
    } else {
      setStep(nextStep);
    }
  };

  const getPublishTypes = (type: PublishType): PublishProductsType[] => {
    const typeMap: Record<PublishType, PublishProductsType[]> = {
      [PublishType.PRODUCT_AND_PRICING]: [PublishProductsType.PRODUCT, PublishProductsType.PRICING],
      [PublishType.PRODUCT]: [PublishProductsType.PRODUCT],
      [PublishType.PRICING]: [PublishProductsType.PRICING],
    };
    return typeMap[type];
  };

  const handlePublish = async () => {
    clearError();
    try {
      const { event_id } = await publishProducts.mutateAsync({
        publish_type: getPublishTypes(publishRequest.type),
        price_scheme_id: publishRequest.priceSchemeId || undefined,
        venues: publishRequest.venues,
        excluded_item_ids: publishRequest.excludedItems.map(({ item_id }) => item_id).filter(Boolean),
        publication_date_time: publishRequest.publicationDate
          ? publishRequest.publicationDate?.toISOString()
          : undefined,
      });

      trackEvent('products_published', {
        publishType: publishRequest.type,
        venueCount: publishRequest.venues.length,
        excludedCount: publishRequest.excludedItems.length,
        isScheduled: !!publishRequest.publicationDate,
      });
      showNotification(successNotification(publishRequest.type, publishRequest.publicationDate?.toISOString()));

      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate(`/items/status/${event_id}`);
    } catch (error) {
      captureException(error, { tags: { action: 'publish_products', publishType: publishRequest.type } });
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      return showNotification(errorNotification(publishRequest.type, undefined, errorMsg));
    }
  };

  return (
    <>
      {step === Steps.type && (
        <SelectPublishType closeModal={closeModal} submitDataToParent={saveData} defaultValues={publishRequest} />
      )}
      {step === Steps.scheme && <SelectPriceScheme submitDataToParent={saveData} defaultValues={publishRequest} />}
      {step === Steps.venues && <SelectVenues submitDataToParent={saveData} defaultValues={publishRequest} />}
      {step === Steps.excludeProducts && (
        <ExcludeProducts submitDataToParent={saveData} defaultValues={publishRequest} />
      )}
      {step === Steps.schedule && (
        <ScheduleView defaultPublicationDate={publishRequest.publicationDate} submitDataToParent={saveData} />
      )}

      {step === Steps.summary && (
        <SummaryView
          submitDataToParent={saveData}
          defaultValues={publishRequest}
          onPublish={handlePublish}
          isLoading={publishProducts.isLoading}
        />
      )}
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </>
  );
};

export const ProductPublishModal = ({ isOpen, closeModal }: IProductPublishModalProps) => {
  return (
    <Modal open={isOpen} image={chefPlating} imageAlt="Picture of chef at the plating station" closeModal={closeModal}>
      <Content closeModal={closeModal} />
    </Modal>
  );
};
